import { Component, Input, ChangeDetectionStrategy } from "@angular/core";

import { BaseComponent, ContentService, Course, EventBusService, Folder, LearnContentType, Lesson, UserService, Video } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { Subject, Subscription } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { AddLearnVideoComponent, EditVideoComponent } from "../../modals";
import { DragulaService } from "ng2-dragula";
import { Router } from "@angular/router";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-manage-education-content",
  templateUrl: "manage-education-content.component.html",
  standalone: false,
})
export class ManageEducationContentComponent extends BaseComponent {
  private _displayedEducationSection: string;
  private initialized = false;
  private queryChanged$ = new Subject<void>();

  @Input() set displayedEducationSection(section) {
    const changed = section !== this._displayedEducationSection;
    this._displayedEducationSection = section;
    if (this.initialized && changed && section) this.loadSection(section);
  }

  get displayedEducationSection() {
    return this._displayedEducationSection;
  }

  public lessons: Array<Lesson> = [];
  public courses: Array<Course> = [];
  public folders: Array<Folder> = [];

  public displayedVideos: Array<Video> = [];
  public hasNoResults = false;

  public loading: boolean = false;
  private videosRequest: Subscription;
  private groupsRequest: Subscription;

  public filters: any = {
    platform: "any",
    brand: "any",
    status: "any",
    product: "any",
    tag: "any",
    level: "any",
    query: "",
    sort_by: "id",
    sort: "desc",
  };

  public limit: number = 100;
  public page: number = 1;
  public totalPages: number = 0;
  public totalVideos: number = 0;
  public syncing: boolean = false;
  constructor(
    userService: UserService,
    public router: Router,
    public appService: AppService,
    public contentService: ContentService,
    public evenbusService: EventBusService,
    private dragulaService: DragulaService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.initialized = true;
    this.queryChanged$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.page = 1;
      this.loadVideos();
    });
    this.loadSection(this._displayedEducationSection || "videos");
    this.contentService.searchTags("").subscribe((tags) => {
      if (tags?.length) {
        tags.forEach((tag) => {
          if (!this.contentService.tagsAssociatedWithVideosMap[tag.id]) {
            this.contentService.tagsAssociatedWithVideos.push(tag);
            this.contentService.tagsAssociatedWithVideosMap[tag.id] = tag;
          }
        });
      }
    });
    this.evenbusService
      .observe(this.evenbusService.types.refreshLearnData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadSection(this._displayedEducationSection || "videos");
      });

    this.dragulaService.dropModel("LESSONS").subscribe(({ targetModel }) => {
      this.contentService.saveLearnGroupOrder(targetModel.map((item) => item.id)).subscribe();
    });

    this.dragulaService.dropModel("FOLDERS").subscribe(({ targetModel }) => {
      this.contentService.saveLearnGroupOrder(targetModel.map((item) => item.id)).subscribe();
    });
  }

  onQueryInput() {
    this.clearVideoResults();
    this.queryChanged$.next();
  }

  loadSection(section: string) {
    if (section === "videos") {
      this.page = 1;
      this.loadVideos();
      return;
    }
    const type = section === "courses" ? "course" : section === "folders" ? "folder" : "lesson";
    this.loadGroups(type as LearnContentType);
  }

  loadVideos() {
    this.groupsRequest?.unsubscribe();
    this.clearVideoResults();
    this.videosRequest?.unsubscribe();
    this.videosRequest = this.contentService
      .searchLearnContent({
        type: "video",
        page: this.page,
        limit: this.limit,
        q: this.filters.query?.trim() || undefined,
        status: this.filters.status,
        platform: this.filters.platform,
        tag: this.filters.tag,
        product: this.filters.product,
        brand: this.filters.brand,
        level: this.filters.level,
        sort_by: this.filters.sort_by,
        sort: this.filters.sort,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.displayedVideos = result.items || [];
          this.totalVideos = result.pagination?.total || 0;
          this.totalPages = this.limit ? Math.ceil(this.totalVideos / this.limit) : 0;
          this.hasNoResults = this.displayedVideos.length === 0;
          this.loading = false;
        },
        error: () => {
          this.displayedVideos = [];
          this.totalVideos = 0;
          this.totalPages = 0;
          this.hasNoResults = true;
          this.loading = false;
        },
      });
  }

  loadGroups(type: LearnContentType) {
    this.videosRequest?.unsubscribe();
    this.groupsRequest?.unsubscribe();
    this.loading = true;
    this.hasNoResults = false;
    this.lessons = [];
    this.courses = [];
    this.folders = [];
    this.groupsRequest = this.contentService
      .searchAllLearnContent(type, { sort_by: "order", sort: "asc" })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          const sorted = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
          if (type === "lesson") this.lessons = sorted;
          if (type === "course") this.courses = sorted;
          if (type === "folder") this.folders = sorted.filter((item) => parseInt(item.id, 10) > 0);
          const current = type === "lesson" ? this.lessons : type === "course" ? this.courses : this.folders;
          this.hasNoResults = current.length === 0;
          this.loading = false;
        },
        error: () => {
          this.hasNoResults = true;
          this.loading = false;
        },
      });
  }

  private clearVideoResults() {
    this.loading = true;
    this.hasNoResults = false;
    this.displayedVideos = [];
    this.totalVideos = 0;
    this.totalPages = 0;
  }

  addVideo() {
    const modalRef = this.appService.showModal(AddLearnVideoComponent, {
      size: "lg",
    });
    modalRef.componentInstance.videoAdded.subscribe((result) => {
      this.loadVideos();
      if (result.type == "video") {
        this.videoClicked(result.result);
      } else if (result.type == "playlist") {
        alert("Playlist added successfully!");
      }
    });
  }

  sync() {
    if (!this.syncing) {
      this.syncing = true;
      this.contentService.syncVideos().subscribe((result: any) => {
        this.syncing = false;
        alert("Syncing complete! Added " + (result.ids && result.ids.length ? result.ids.length : 0) + " new videos.");
        this.page = 1;
        this.loadVideos();
      });
    }
  }

  resetFilters() {
    this.filters = {
      platform: "any",
      brand: "any",
      status: "any",
      product: "any",
      tag: "any",
      level: "any",
      query: "",
      sort_by: "id",
      sort: "desc",
    };
    this.page = 1;
    this.loadVideos();
  }

  filter() {
    this.page = 1;
    this.loadVideos();
  }

  paginate(direction) {
    this.page = this.page + (direction == "next" ? 1 : -1);
    this.loadVideos();
  }

  videoClicked(args, index?) {
    const modalRef = this.appService.showModal(EditVideoComponent, {
      size: "lg",
    });
    modalRef.componentInstance.video = args;
    modalRef.componentInstance.index = index;
    modalRef.result.then(
      (result) => {
        if (result && result.deleted) {
          this.displayedVideos.splice(result.index, 1);
          this.hasNoResults = this.displayedVideos.length === 0;
        } else if (result && result.saved) {
          this.loadVideos();
        }
      },
      () => {}
    );
  }

  showAddEditLearnGroup(type?, item?) {
    this.router.navigate([`/content/manage_learn_content/${type}/${item?.id || "new"}`]);
  }
}
